import React from 'react';
import './App.css';
import mo360Logo from './assets/MO360ECMLogo.png';
import spectreIcon from './assets/spectre_icon.ico';
import Header from './components/Header/Header';
import SessionExpiredDialog from './components/SessionExpiredDialog/SessionExpiredDialog';
import LoginFailureDialog from './components/LoginFailureDialog/LoginFailureDialog';
import { AuthProvider, useAuth } from './context/AuthContext';

/**
 * Main content component that uses auth context
 */
function AppContent() {
    const { session, isLoading, isAuthenticated, sessionExpired, loginFailed, handleSessionExpiredLogin, handleLoginFailureRetry } = useAuth();
    const [isHome, setIsHome] = React.useState(window.location.pathname === '/');

    React.useEffect(() => {
        const handleNavigation = () => setIsHome(window.location.pathname === '/');
        window.addEventListener('popstate', handleNavigation);
        window.addEventListener('single-spa:routing-event', handleNavigation);
        return () => {
            window.removeEventListener('popstate', handleNavigation);
            window.removeEventListener('single-spa:routing-event', handleNavigation);
        };
    }, []);

    // Route protection: redirect if accessing route without permission
    React.useEffect(() => {
        const path = window.location.pathname;
        if (isAuthenticated && session) {
            if (path.startsWith('/ecm') && !session.ecmUser) {
                window.history.pushState(null, '', '/');
                setIsHome(true);
            }
            if (path.startsWith('/spectre') && !session.spectreUser) {
                window.history.pushState(null, '', '/');
                setIsHome(true);
            }
        }
    }, [isAuthenticated, session]);

    const navigateTo = (path: string) => {
        window.history.pushState(null, '', path);
        setIsHome(false);
    };

    // Show loading state while checking session or awaiting login redirect
    if (isLoading || (!isAuthenticated && !sessionExpired && !loginFailed)) {
        return (
            <div className="App loading-container">
                <div className="loading-spinner"></div>
                <span>Loading...</span>
            </div>
        );
    }

    // Show access denied when user has no access to any application
    const hasNoAccess = isAuthenticated && !session?.ecmUser && !session?.spectreUser;
    if (hasNoAccess) {
        return (
            <>
                <Header />
                <div className="App">
                    <div className="access-denied-container">
                        <div className="access-denied-icon">🔒</div>
                        <h2 className="access-denied-title">Access Denied</h2>
                        <p className="access-denied-message">
                            You don't have access to the applications. Please request access.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            {isHome && (
                <div className="App">
                    <div className="tiles-container">
                        {/* Show ECM tile only if user has ECM access */}
                        {(isAuthenticated && session?.ecmUser) && (
                            <div className="tile-card" onClick={() => navigateTo('/ecm')}>
                                <img src={mo360Logo} className="tile-image ecm-tile-image" alt="ECM Logo" />
                                <div className="tile-text">ECM</div>
                            </div>
                        )}
                        {/* Show Spectre tile only if user has Spectre access */}
                        {(isAuthenticated && session?.spectreUser) && (
                            <div className="tile-card" onClick={() => navigateTo('/spectre')}>
                                <img src={spectreIcon} className="tile-image" alt="Spectre Logo" />
                                <div className="tile-text">Spectre</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <SessionExpiredDialog open={sessionExpired} onLogin={handleSessionExpiredLogin} />
            <LoginFailureDialog open={loginFailed} onRetry={handleLoginFailureRetry} />
        </>
    );
}

/**
 * Root App component wrapped with AuthProvider
 */
function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
